import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import DashboardLayout from "./components/Layout";
import useRole from "./components/useRole";
import { getEventReport } from "../../api/plan";

const money = (value) => Number(value || 0).toFixed(2);

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date)) return "";
  return date.toLocaleDateString("az-AZ", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

const formatTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date)) return "";
  return date.toLocaleTimeString("az-AZ", { hour: "2-digit", minute: "2-digit" });
};

// One line of the stacked view used on phones
const CardRow = ({ label, note, value, strong }) => (
  <div className="flex items-start justify-between gap-3 px-4 py-3">
    <div className="min-w-0">
      <div className={`text-sm ${strong ? "font-semibold text-gray-900" : "text-gray-700"}`}>{label}</div>
      {note && <div className="text-xs text-gray-500 mt-0.5">{note}</div>}
    </div>
    <div className={`text-sm whitespace-nowrap ${strong ? "font-bold text-gray-900" : "text-gray-900"}`}>{value}</div>
  </div>
);

const cell = "border border-gray-300 px-3 py-2 text-sm";
const num = `${cell} text-right whitespace-nowrap`;

const ReportsPage = () => {
  const { eventId } = useParams();
  const { isAdmin } = useRole();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  // The browser names the PDF after the page title
  const handlePrint = () => {
    const previousTitle = document.title;
    document.title = `Hesabat - ${report?.event?.title || ""}`.trim();
    const restore = () => {
      document.title = previousTitle;
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    window.print();
  };

  useEffect(() => {
    getEventReport(eventId)
      .then((res) => setReport(res?.data?.data))
      .catch((e) => setError(e?.response?.data?.message || "Hesabatı yükləmək olmadı."));
  }, [eventId]);

  return (
    <DashboardLayout id={eventId}>
      <div className="mt-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold mb-1">🧾 Hesabat</h2>
            <p className="text-sm text-gray-500 mb-5">
              Tədbir üzrə satış, tutulmalar və sifarişçiyə ödəniləcək məbləğ. Faizləri və qiymətləri
              "Parametrlər" bölməsində dəyişə bilərsiniz.
            </p>
          </div>
          {report && (
            <button
              type="button"
              onClick={handlePrint}
              className="no-print bg-black text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-800"
            >
              🖨️ Çap et
            </button>
          )}
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
        {!report && !error && <p className="text-sm text-gray-500">Yüklənir...</p>}

        {report && (
          <div id="report-print-area" className="bg-white rounded-xl shadow p-4">
            {/* Only on paper: the sheet needs its own heading */}
            <div className="print-only mb-3">
              <h1 className="text-lg font-bold">{report.event?.title}</h1>
              <p className="text-xs text-gray-600">
                {formatDate(report.event?.date)} {formatTime(report.event?.date)}
                {report.event?.place ? ` · ${report.event.place}` : ""}
              </p>
            </div>

            {/* Phones: the same figures line by line, so nothing scrolls sideways */}
            <div className="report-card-view space-y-3">
              <div className="rounded-xl border border-gray-200 px-4 py-3">
                <div className="font-semibold text-gray-900">{report.event?.title}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {formatDate(report.event?.date)} {formatTime(report.event?.date)}
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
                <CardRow
                  label="Biletlərin satışı"
                  note={`${report.sold?.count} bilet`}
                  value={`${money(report.sold?.amount)} ₼`}
                  strong
                />
                <CardRow
                  label={`Korporativ biletlər (${report.settings?.cooperativeCommission}%)`}
                  note={`${report.cooperative?.count} bilet · ${money(report.cooperative?.amount)} ₼`}
                  value={`− ${money(report.deductions?.cooperativeCommission)} ₼`}
                />
                <CardRow
                  label={`Dəvətnamələr (${money(report.settings?.invitationPrice)} ₼)`}
                  note={`${report.invitations?.count} / ${report.invitations?.limit} nəfər${
                    report.invitations?.tickets > 0 && report.invitations?.tickets !== report.invitations?.count
                      ? ` · ${report.invitations.tickets} bilet`
                      : ""
                  }`}
                  value={`− ${money(report.deductions?.invitations)} ₼`}
                />
                <CardRow
                  label={`Toplam satış (${report.settings?.salesCommission}%)`}
                  note={`${money(report.totals?.sales)} ₼ satışdan`}
                  value={`− ${money(report.deductions?.salesCommission)} ₼`}
                />
                <CardRow label="Sosial şəbəkələrdə reklam" value={`− ${money(report.deductions?.advertising)} ₼`} />
                <CardRow
                  label="Access control"
                  note={`${report.settings?.accessControlPeopleCount} nəfər · ${money(report.settings?.accessControlPersonPrice)} ₼`}
                  value={`− ${money(report.deductions?.accessControl)} ₼`}
                />
                <CardRow label="Xidmət haqqı" value={`− ${money(report.deductions?.serviceFee)} ₼`} />
              </div>

              <div className="rounded-xl border border-gray-200 bg-gray-50 divide-y divide-gray-200">
                <CardRow label="Tutulmalar" value={`${money(report.totals?.deductions)} ₼`} />
                <CardRow label="Toplam satış" value={`${money(report.totals?.sales)} ₼`} />
                <CardRow label="Biletlər" note={`${report.totals?.tickets} bilet`} value={`${money(report.totals?.amount)} ₼`} />
                <CardRow label="Sifarişçiyə təhvil verilən məbləğ" value={`${money(report.totals?.payout)} ₼`} strong />
              </div>
            </div>

            <div className="report-table-view overflow-x-auto">
            <table className="min-w-max w-full border-collapse">
              <thead>
                <tr className="bg-gray-100 text-sm font-semibold text-gray-700">
                  <th className={cell} rowSpan={2}>№</th>
                  <th className={cell} rowSpan={2}>Tarix</th>
                  <th className={cell} rowSpan={2}>Vaxt</th>
                  <th className={cell} rowSpan={2}>Tədbirin adı</th>
                  <th className={cell} rowSpan={2}>Tutulmalar</th>
                  <th className={cell} rowSpan={2}>Toplam satış</th>
                  <th className={cell} colSpan={2}>Biletlərin satışı</th>
                  <th className={cell} rowSpan={2}>
                    Sifarişçiyə təhvil<br />verilən məbləğ
                  </th>
                </tr>
                <tr className="bg-gray-100 text-sm font-semibold text-gray-700">
                  <th className={cell}>Miqdar</th>
                  <th className={cell}>Məbləğ</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={cell}>1</td>
                  <td className={cell}>{formatDate(report.event?.date)}</td>
                  <td className={cell}>{formatTime(report.event?.date)}</td>
                  <td className={cell}>{report.event?.title}</td>
                  <td className={num} />
                  <td className={num} />
                  <td className={num}>{report.sold?.count}</td>
                  <td className={num}>{money(report.sold?.amount)}</td>
                  <td className={num} />
                </tr>

                <tr>
                  <td className={cell} colSpan={3} />
                  <td className={cell}>Korporativ biletlər ({report.settings?.cooperativeCommission}%)</td>
                  <td className={num}>{money(report.deductions?.cooperativeCommission)}</td>
                  <td className={num} />
                  <td className={num}>{report.cooperative?.count}</td>
                  <td className={num}>{money(report.cooperative?.amount)}</td>
                  <td className={num} />
                </tr>

                <tr>
                  <td className={cell} colSpan={3} />
                  <td className={cell}>
                    Dəvətnamələr ({money(report.settings?.invitationPrice)} ₼)
                    {report.invitations?.count > report.invitations?.limit && (
                      <span className="ml-2 text-xs text-red-600">limit: {report.invitations?.limit}</span>
                    )}
                  </td>
                  <td className={num}>{money(report.deductions?.invitations)}</td>
                  <td className={num} />
                  <td className={num}>
                    {report.invitations?.count}
                    <span className="text-gray-400"> / {report.invitations?.limit}</span>
                    {report.invitations?.tickets > 0 && report.invitations?.tickets !== report.invitations?.count && (
                      <div className="text-[11px] text-gray-400">{report.invitations.tickets} bilet</div>
                    )}
                  </td>
                  <td className={num} />
                  <td className={num} />
                </tr>

                <tr>
                  <td className={cell} colSpan={3} />
                  <td className={cell}>Toplam satış ({report.settings?.salesCommission}%)</td>
                  <td className={num}>{money(report.deductions?.salesCommission)}</td>
                  <td className={num}>{money(report.totals?.sales)}</td>
                  <td className={num} />
                  <td className={num} />
                  <td className={num} />
                </tr>

                <tr>
                  <td className={cell} colSpan={3} />
                  <td className={cell}>Sosial şəbəkələrdə reklam</td>
                  <td className={num}>{money(report.deductions?.advertising)}</td>
                  <td className={num} />
                  <td className={num} />
                  <td className={num} />
                  <td className={num} />
                </tr>

                <tr>
                  <td className={cell} colSpan={3} />
                  <td className={cell}>
                    Access control ({report.settings?.accessControlPeopleCount} nəfər,{" "}
                    {money(report.settings?.accessControlPersonPrice)} ₼)
                  </td>
                  <td className={num}>{money(report.deductions?.accessControl)}</td>
                  <td className={num} />
                  <td className={num} />
                  <td className={num} />
                  <td className={num} />
                </tr>

                <tr>
                  <td className={cell} colSpan={3} />
                  <td className={cell}>Xidmət haqqı</td>
                  <td className={num}>{money(report.deductions?.serviceFee)}</td>
                  <td className={num} />
                  <td className={num} />
                  <td className={num} />
                  <td className={num} />
                </tr>

                <tr className="bg-gray-100 font-bold">
                  <td className={cell} colSpan={4}>Yekun</td>
                  <td className={num}>{money(report.totals?.deductions)}</td>
                  <td className={num}>{money(report.totals?.sales)}</td>
                  <td className={num}>{report.totals?.tickets}</td>
                  <td className={num}>{money(report.totals?.amount)}</td>
                  <td className={num}>{money(report.totals?.payout)}</td>
                </tr>
              </tbody>
            </table>
            </div>
          </div>
        )}

        {/* Tixy's own figures: only its admins see them, and they stay off the organizer's printout */}
        {isAdmin && report?.tixy && (
          <div className="no-print mt-6">
            <h3 className="text-lg font-bold mb-1">💰 Tixy-nin xalis gəliri</h3>
            <p className="text-sm text-gray-500 mb-3">Yalnız adminlər görür və çap olunmur.</p>
            <div className="bg-white rounded-xl shadow divide-y divide-gray-100 max-w-xl">
              <CardRow
                label="Tixy-nin gəliri"
                note="Komissiyalar və xidmət haqları (tutulmalar)"
                value={`${money(report.tixy.revenue)} ₼`}
              />
              <CardRow
                label={`Epoint komissiyası (${report.tixy.epointCommission}%)`}
                note={`${money(report.tixy.online)} ₼ onlayn ödənişdən`}
                value={`− ${money(report.tixy.epoint)} ₼`}
              />
              <CardRow
                label={`Sadələşdirilmiş vergi (${report.tixy.taxRate}%)`}
                note={`${money(report.tixy.revenue)} ₼ gəlirdən`}
                value={`− ${money(report.tixy.tax)} ₼`}
              />
              <CardRow label="Xalis gəlir" value={`${money(report.tixy.net)} ₼`} strong />
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ReportsPage;
